import { formatNOK } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import { outstandingTotal } from "@/v2-backoffice-client/legalBilling/legalBilling.utils"
import type {
  LegalBillingInvoiced,
  LegalBillingTotals,
} from "@/v2-backoffice-client/legalBilling/legalBilling.types"

export type LegalBillingTotalsSummaryProps = {
  totals: LegalBillingTotals
  invoiced: LegalBillingInvoiced
  className?: string
  /** Renders the larger range-level variant with labelled figures. */
  isRoot?: boolean
}

// The figures shown at every rollup level. One component so a tenant, tag group, batch and
// the whole range can never present the same numbers in different terms — the failure T29's
// revision history describes is two surfaces disagreeing on one batch, and four
// inconsistent renderings of the same struct is the same bug one level down.
//
// Outstanding (total minus already invoiced) is the headline: "what did they spend" stops
// being the useful question the moment part of it is billed.
function LegalBillingTotalsSummary(props: LegalBillingTotalsSummaryProps) {
  const { totals, invoiced, className, isRoot } = props

  const outstanding = outstandingTotal(totals, invoiced)

  return (
    <div
      className={cn(
        "legal-billing-totals flex items-center gap-3",
        isRoot && "legal-billing-totals-root gap-5",
        className,
      )}
    >
      <Figure
        label="Outstanding"
        value={formatNOK(outstanding)}
        isEmphasis
        isRoot={isRoot}
      />
      <Figure label="Total" value={formatNOK(totals.Total)} isRoot={isRoot} />
      <Figure
        label="Invoiced"
        value={formatNOK(invoiced.Total)}
        isRoot={isRoot}
      />
      <Figure
        label="Documents"
        value={String(totals.DocumentCount)}
        isRoot={isRoot}
      />

      {/* A pending price can still fail and remove itself, so a total containing one is
          not a number to invoice yet. Stated on every level that contains pending rows,
          not just the range total — an admin invoices a tag group, not the range. */}
      {totals.IsProvisional ? (
        <Badge variant="warning" className="legal-billing-provisional-badge">
          Provisional · {totals.PendingCount} pending ·{" "}
          {formatNOK(totals.PendingTotal)} unsettled
        </Badge>
      ) : null}

      {/* Failed purchases are never charged and never listed as lines. Without this the
          batch of 7 that shows 3 lines reads as a bug rather than as 4 exclusions. */}
      {totals.FailedCount > 0 ? (
        <span className="legal-billing-failed-note text-xs text-muted-foreground">
          {totals.FailedCount} failed, excluded
        </span>
      ) : null}
    </div>
  )
}

type FigureProps = {
  label: string
  value: string
  isEmphasis?: boolean
  isRoot?: boolean
}

function Figure(props: FigureProps) {
  const { label, value, isEmphasis, isRoot } = props

  return (
    <span className="legal-billing-figure flex items-baseline gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={cn(
          "tabular-nums",
          isEmphasis && "font-semibold",
          isRoot ? "text-base" : "text-sm",
        )}
      >
        {value}
      </span>
    </span>
  )
}

export default LegalBillingTotalsSummary
